class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // ✅ init required agreements logic (drawer)
    this.initAgreements();
  }

  // ✅ Required agreements: disable checkout until both are checked
  initAgreements() {
    // Drawer content can re-render, so always query fresh elements
    const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
    const privacyCb = this.querySelector('#CartDrawer-Agreement-Privacy');
    const termsCb = this.querySelector('#CartDrawer-Agreement-Terms');
    const hint = this.querySelector('#CartDrawer-AgreementHint');

    // If the agreements block doesn't exist (e.g. empty cart), do nothing
    if (!checkoutBtn || !privacyCb || !termsCb) return;

    const updateState = () => {
      const ok = privacyCb.checked && termsCb.checked;

      // Keep original empty-cart disable behavior too
      if (this.classList.contains('is-empty')) {
        checkoutBtn.setAttribute('disabled', 'disabled');
      } else if (ok) {
        checkoutBtn.removeAttribute('disabled');
      } else {
        checkoutBtn.setAttribute('disabled', 'disabled');
      }

      if (hint) hint.hidden = ok;
    };

    // Ensure starting state is correct
    updateState();

    // Update on changes
    privacyCb.addEventListener('change', updateState);
    termsCb.addEventListener('change', updateState);

    // If user clicks disabled button (some browsers won't fire click),
    // we also show hint on mousedown/pointerdown.
    const showHintIfNeeded = (e) => {
      const ok = privacyCb.checked && termsCb.checked;
      if (!ok) {
        if (hint) hint.hidden = false;
        // Prevent any accidental submit attempts
        e.preventDefault();
        e.stopPropagation();
      }
    };

    checkoutBtn.addEventListener('click', showHintIfNeeded);
    checkoutBtn.addEventListener('mousedown', showHintIfNeeded);
    checkoutBtn.addEventListener('pointerdown', showHintIfNeeded);
    checkoutBtn.addEventListener('touchstart', showHintIfNeeded, { passive: false });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
		if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      // ✅ Re-init agreements after drawer HTML has been re-rendered
      this.initAgreements();

      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
